/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the body sample.

   The Studio does not edit spec CONTENT (that is the Designer's job on a
   real document); it edits the FORMATTING RULES a level applies. So the
   body here is a realistic sample, GENERATED FROM THE OPEN FORMA'S OWN
   LEVELS (`sampleSpec.ts` — as many levels deep as the forma defines, not
   a fixed snippet), rendered through the exact same brain the export uses
   (`shared/body-layout.ts`) — the identical CSS classes and inline styles
   `numbering.ts` decorates the Designer's live document with (`dsg-block`,
   `dsg-level-N`, `dsg-num`), so a forma edit restyles this sample exactly
   as it would restyle real content.

   Clicking a sample paragraph points the Levels palette at that level —
   the fastest path from "I see it wrong" to "fix it" for a lay person —
   and grows that level's direct-manipulation affordances:

   - SPACING: a band that FILLS each of its gaps (SpacingBand). The band
     is MEASURED off the rendered paragraphs, never predicted from the
     config; CSS collapses `margin-bottom`/`margin-top` into one gap, and
     the old predicted grip drew half of one margin and so landed on the
     text instead of the space (Josh, 2026-08-22).
   - INDENT: Word's two markers, on the paragraph itself (IndentHandles),
     sharing one write path with the ruler.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import {
  applyTextCase,
  bodyParagraphCss,
  computeNumbering,
  numberColumnPx,
  resolveBodyLayout,
} from '../../shared/body-layout';
import type { FormaEdit } from './panels/FormaPalettes';
import { generateStudioSampleBlocks, sampleMaxLevel } from './sampleSpec';
import { SpacingBand } from './SpacingBand';
import { IndentHandles } from './IndentHandles';
import { LevelContextMenu } from './LevelContextMenu';

function cssVarStyle(css: ReturnType<typeof bodyParagraphCss>): React.CSSProperties {
  return {
    paddingLeft: css.paddingLeft,
    textIndent: css.textIndent,
    marginTop: css.marginTop,
    marginBottom: css.marginBottom,
    textAlign: css.textAlign,
    fontWeight: css.fontWeight,
    fontStyle: css.fontStyle,
    textDecoration: css.textDecoration,
    textTransform: css.textTransform,
    ...(css.fontFamily ? { fontFamily: css.fontFamily } : {}),
    ...(css.fontSize ? { fontSize: css.fontSize } : {}),
    ...(css.color ? { color: css.color } : {}),
  };
}

export interface StudioBodyProps {
  forma: StandardConfig | null;
  /** Which level is lit up (the Levels palette's target), or null. */
  levelFocus: number | null;
  /** Every selected level (the Format Bar's targets) — all light up. */
  selectedLevels?: number[];
  /** Click a sample paragraph → select its level (Ctrl toggles, Shift
   *  ranges) and point the Levels palette at it. */
  onLevelClick: (level: number, mods?: { ctrl?: boolean; shift?: boolean }) => void;
  /** Present → the focused level grows the drag-to-space bands
   *  (Space Before / Between / After) and the two indent markers. */
  edit?: FormaEdit;
  /** Override the generated sample — not used by any current caller; kept
   *  for tests / future callers that want fixed content instead of the
   *  forma-derived generator. */
  blocks?: ContentBlock[];
  /** Pages view: the page break drawn at the top of row i (key = row index;
   *  key = row count → after the last row). Absent → no breaks (Continuous). */
  pageBreaks?: Record<number, React.ReactNode>;
}

/** One spacing band the focused level grows in one of its own gaps. */
interface BandPlan {
  key: string;
  which: 'before' | 'same' | 'after';
  /** Index of the block ABOVE the gap (-1 → the element before the body). */
  aboveIdx: number;
  /** Index of the block BELOW the gap (n → the element after the body). */
  belowIdx: number;
  valuePt: number;
  frozenNote?: string;
  leftPx: number;
  label: string;
}

export function StudioBody({
  forma,
  levelFocus,
  selectedLevels,
  onLevelClick,
  edit,
  blocks,
  pageBreaks,
}: StudioBodyProps) {
  // Regenerated straight off the forma reference every render — no stale
  // memo of "how many levels this forma had last time". Adding, removing,
  // or importing over a level changes `forma.levels.length` and the sample
  // follows immediately (contract: see sampleSpec.ts's 2026-08-13 note).
  const sampleBlocks = React.useMemo(
    () => blocks ?? generateStudioSampleBlocks(forma),
    [blocks, forma]
  );
  const maxLevel = sampleMaxLevel(forma);
  const layouts = React.useMemo(() => resolveBodyLayout(forma), [forma]);
  const refs = React.useMemo(() => computeNumbering(sampleBlocks, forma), [sampleBlocks, forma]);

  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const [levelMenu, setLevelMenu] = React.useState<{ x: number; y: number; level: number } | null>(
    null
  );
  const blockEls = React.useRef<Array<HTMLDivElement | null>>([]);

  // Every row's resolved geometry, computed once and reused by both the
  // paragraph render and the overlay — the overlay must never re-derive
  // what the paragraph was actually given.
  const rows = React.useMemo(
    () =>
      sampleBlocks.map((b, i) => {
        const level = Math.max(1, Math.min(9, b.level || 1));
        const layout = layouts[level - 1];
        const prevLevel = i > 0 ? sampleBlocks[i - 1].level : null;
        const nextLevel = i < sampleBlocks.length - 1 ? sampleBlocks[i + 1].level : null;
        const css = bodyParagraphCss(layout, {
          sameStyleAsPrev: prevLevel === level,
          sameStyleAsNext: nextLevel === level,
        });
        return { block: b, level, layout, prevLevel, nextLevel, css };
      }),
    [sampleBlocks, layouts]
  );

  const focusIdx = levelFocus != null ? levelFocus - 1 : null;
  const focusLevel = forma?.levels?.[focusIdx ?? -1] ?? null;
  const gripsOn = !!(edit && focusLevel && levelFocus != null);

  const bands = React.useMemo<BandPlan[]>(() => {
    if (!gripsOn || !focusLevel || levelFocus == null) return [];
    const out: BandPlan[] = [];
    rows.forEach((r, i) => {
      if (r.level !== levelFocus) return;
      const leftPx = parseFloat(r.css.paddingLeft) || 0;
      if (r.prevLevel !== r.level) {
        out.push({
          key: `sb${i}`,
          which: 'before',
          aboveIdx: i - 1,
          belowIdx: i,
          valuePt: focusLevel.spaceBefore || 0,
          leftPx,
          label: 'Space before',
        });
      } else {
        // The gap BETWEEN two paragraphs of the same level: Word derives it
        // from Space After, so this band edits Space After — exactly the
        // classic canvas's semantics.
        out.push({
          key: `sm${i}`,
          which: 'same',
          aboveIdx: i - 1,
          belowIdx: i,
          valuePt: focusLevel.spaceAfter || 0,
          frozenNote: focusLevel.noSpaceSameStyle ? 'no space' : undefined,
          leftPx,
          label: 'Space between (edits space after)',
        });
      }
      if (r.nextLevel !== r.level) {
        out.push({
          key: `sa${i}`,
          which: 'after',
          aboveIdx: i,
          belowIdx: i + 1,
          valuePt: focusLevel.spaceAfter || 0,
          leftPx,
          label: 'Space after',
        });
      }
    });
    return out;
  }, [gripsOn, focusLevel, levelFocus, rows]);

  const commitBand = React.useCallback(
    (which: BandPlan['which'], v: number) => {
      if (!edit || focusIdx == null) return;
      const field = which === 'before' ? 'spaceBefore' : 'spaceAfter';
      edit((f) => {
        const levels = [...(f.levels || [])];
        if (!levels[focusIdx]) return f;
        levels[focusIdx] = { ...levels[focusIdx], [field]: Math.max(0, Math.min(120, v)) };
        return { ...f, levels };
      });
    },
    [edit, focusIdx]
  );

  // The gap above the first sample paragraph and below the last one live
  // OUTSIDE the body element (margins collapse through `.dsg-doc`), so those
  // two bands measure against the body's own siblings on the sheet.
  const edgeEl = React.useCallback((side: 'prev' | 'next'): Element | null => {
    const host = hostRef.current;
    if (!host) return null;
    return side === 'prev' ? host.previousElementSibling : host.nextElementSibling;
  }, []);

  const focusBlockEls = React.useCallback(
    () =>
      rows
        .map((r, i) => (r.level === levelFocus ? blockEls.current[i] : null))
        .filter((el): el is HTMLDivElement => !!el),
    [rows, levelFocus]
  );

  const focusRow = rows.find((r) => r.level === levelFocus) || null;

  return (
    <div
      className="dsg-doc sfs-studio-body"
      ref={hostRef}
      role="group"
      aria-label="Sample section content"
      data-sample-level-count={maxLevel}
    >
      {rows.map((r, i) => {
        const prefix = refs[i]?.prefix || '';
        const text = applyTextCase(r.block.text, r.layout);
        const lit = selectedLevels ? selectedLevels.includes(r.level) : levelFocus === r.level;
        return (
          <div
            key={r.block.id}
            ref={(el) => {
              blockEls.current[i] = el;
            }}
            className={`dsg-block dsg-level-${r.level}${lit ? ' sfs-level-lit' : ''}`}
            style={cssVarStyle(r.css)}
            role="button"
            tabIndex={0}
            aria-label={`Level ${r.level} sample paragraph — click to style level ${r.level}`}
            onClick={(e) =>
              onLevelClick(r.level, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey })
            }
            onContextMenu={(e) => {
              // What you right-click decides the menu: a level row gets its
              // own (Level style…, jump to a group, Same as Typography…).
              // The sample row is never a text editor, so the browser menu
              // has nothing to offer here.
              e.preventDefault();
              setLevelMenu({ x: e.clientX, y: e.clientY, level: r.level });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLevelClick(r.level);
              }
            }}
          >
            {pageBreaks?.[i]}
            {prefix && (
              <span
                className="dsg-num"
                aria-hidden="true"
                style={{ minWidth: numberColumnPx(r.layout) }}
              >
                {prefix}
              </span>
            )}
            {text}
          </div>
        );
      })}
      {pageBreaks?.[rows.length]}

      {levelMenu && (
        <LevelContextMenu
          at={levelMenu}
          level={levelMenu.level}
          forma={forma}
          edit={edit}
          onLevelStyle={onLevelClick}
          onClose={() => setLevelMenu(null)}
        />
      )}

      {gripsOn && (
        <div className="sfs-body-overlay">
          {bands.map((b) => (
            <SpacingBand
              key={b.key}
              hostRef={hostRef}
              getAbove={() =>
                b.aboveIdx < 0 ? edgeEl('prev') : (blockEls.current[b.aboveIdx] ?? null)
              }
              getBelow={() =>
                b.belowIdx >= rows.length ? edgeEl('next') : (blockEls.current[b.belowIdx] ?? null)
              }
              valuePt={b.valuePt}
              label={b.label}
              ariaLabel={`Level ${levelFocus} ${b.label.toLowerCase()}`}
              min={0}
              max={120}
              leftPx={b.leftPx}
              frozenNote={b.frozenNote}
              onCommit={(v) => commitBand(b.which, v)}
            />
          ))}
          {focusRow && focusIdx != null && forma && edit && (
            <IndentHandles
              hostRef={hostRef}
              getBlocks={focusBlockEls}
              forma={forma}
              levelIdx={focusIdx}
              padLeftPx={parseFloat(focusRow.css.paddingLeft) || 0}
              textIndentPx={parseFloat(focusRow.css.textIndent) || 0}
              edit={edit}
            />
          )}
        </div>
      )}
    </div>
  );
}
